import React, {useEffect, useMemo, useRef, useState} from 'react'
import IconNoticeRed from '@svg/common/ic_notice_red.svg'
import WizButton from '@component/buttons/WizButton'
import {createLog} from '@util/logs'
import ChangeEmailModal from '@feature/account/pages/setting/ChangeEmailModal/ChangeEmailModal'
import {useImportantAlertsContext} from '@hook/useImportantAlerts'
import {useRouter} from 'next/router'
import {useTranslation} from '@lib/intl/useTranslations'
import TagText from '@component/Text/TagText'
import useQueryGetTodayDailyReport from '@hook/query/user/account/report/useQueryGetTodayDailyReport'
import Text from '@component/Text/Text'
import useFormatNumber from '@hook/useFormatNumber'
import AccountImportantAlertModal from '@feature/account/components/importantAlert/AccountImportantAlertModal'
import Span from '@component/Text/Span'
import {getLinkUrl, URL_ACCOUNT_API, URL_LEADERBOARD_COPY} from '@constant/url'
import useQueryFetchMe from '@hook/query/account/useQueryFetchMe'
import {addErrorLog} from '@constant/log'
import {regions} from '@constant/regions'
import WizImage from '@component/images/WizImage'
import IconClose from '@svg/common/ic_close.svg'
import IconNext from '@svg/common/ic_btn_next.svg'
import useQueryGetUserDailyEarnings from '@hook/query/portfolio/useQueryGetUserDailyEarnings'
import {useLoginStore} from '@store/LoginStore'
import Link from 'next/link'
import SharePortfolioOptionModal from '@feature/portfolio/components/SharePortfolioOptionModal/SharePortfolioOptionModal'
import useFormatDate from '@hook/useFormatDate'
import DailyReportModal from '@feature/common/components/DailyReportModal/DailyReportModal'

interface IProps {
    className?: string
}

const REMOVE_ALERT_PROFILE = 'account_profile_setting_alert'

const AccountImportantAlertContainer: React.FC<IProps> = ({className}) => {
    const router = useRouter()
    const t = useTranslation()
    const loginId = useLoginStore(state => state.id)
    const {displayBalance, displayPercent} = useFormatNumber()
    const {
        alertLength,
        alertList,
        isLoading,
        invoiceAlertLength,
        invoiceAlertList,
        settingAlertLength,
        settingAlertList,
        proAlertLength,
        proAlertList,
    } = useImportantAlertsContext()
    const {formattedUTCOffset} = useFormatDate()

    const changeEmailModalRef = useRef<any>(null)
    const dailyReportModalRef = useRef<any>(null)
    const importantAlertModalRef = useRef<any>(null)
    const sharePortfolioModalRef = useRef<any>(null)

    const {data} = useQueryGetTodayDailyReport(false)
    const {data: me, isLoading: isLoadingMe} = useQueryFetchMe(undefined, false)
    const {data: dailyEarning} = useQueryGetUserDailyEarnings(loginId, undefined, 3)

    const renderUserProfitText = useMemo(() => {
        return `${displayBalance(data?.earning, {
            showPreSign: true,
            isShowMinusSymbol: false,
        })}(${data?.score >= 0 ? '+' : ''}${displayPercent(data?.score)})`
    }, [data])

    const [ignoredProfile = [], setIgnoredProfile] = useState<boolean>(false)
    const [locationInfo, setLocationInfo] = useState(null)
    const [isShowReportText, setIsShowReportText] = useState<'positive' | 'negative' | ''>('')

    const list = useMemo(() => {
        return [...invoiceAlertList, ...alertList, ...proAlertList, ...settingAlertList]
    }, [alertList, invoiceAlertList, settingAlertList, proAlertList])

    useEffect(() => {
        const createdAt = data?.created_at

        const createdUTCDate = new Date((createdAt ?? 0) * 1000).toISOString().split('T')[0]
        const currentUTCDate = new Date().toISOString().split('T')[0]

        const isCheckExchange = Boolean(me?.active_exchanges !== undefined && me?.active_exchanges.length > 0)

        const isCheckCondition = isCheckExchange && data && createdUTCDate === currentUTCDate

        if (!dailyEarning || dailyEarning?.length === 0 || !localStorage || !isCheckCondition) return

        const earnings = dailyEarning?.map(item => item?.earning)
        const allPositive = earnings?.every(item => item > 0)
        const allNegative = earnings?.every(item => item < 0)

        if (allPositive) {
            setIsShowReportText('positive')
        } else if (allNegative) {
            setIsShowReportText('negative')
        } else {
            setIsShowReportText('')
        }
    }, [dailyEarning, me?.active_exchanges, data])

    useEffect(() => {
        const stored = localStorage?.getItem(REMOVE_ALERT_PROFILE)

        if (stored) {
            try {
                const res = JSON.parse(stored)
                setIgnoredProfile(res)
            } catch {
                // 파싱 에러 시 무시
            }
        } else {
            setIgnoredProfile(false)
        }
    }, [me?.thumbnail, me?.flags])

    useEffect(() => {
        // 즉시 실행 함수로 Fetch
        ;(async () => {
            try {
                if (localStorage && localStorage?.getItem('user_location')) {
                    setLocationInfo(localStorage?.getItem('user_location'))
                } else {
                    const geoRes = await fetch('https://get.geojs.io/v1/ip/geo.json')
                    if (!geoRes.ok) {
                        console.error('GeoJS API 응답 오류:', geoRes.statusText)
                        return
                    }

                    const geoData = await geoRes.json()

                    // 필요한 정보만 저장 (예: country_code)
                    const countryCode = geoData.country_code
                    localStorage.setItem('user_location', countryCode)
                    setLocationInfo(countryCode)
                }
            } catch (error) {
                addErrorLog(`[Custom Error] User Location Fetch Failed`, {error: error})
                // 에러 시 null(또는 에러 상태)를 그대로 두거나, 대체 데이터 표시 가능
                setLocationInfo(null)
            }
        })()
    }, [])

    const userRegion = useMemo(() => {
        return regions?.find(item => item?.code?.toUpperCase() === locationInfo?.toUpperCase())
    }, [locationInfo])

    const {rawCondition1, rawCondition2, rawCondition3, rawCondition4} = useMemo(() => {
        return {
            rawCondition1: list?.length > 0,
            rawCondition2: Boolean(data) && Object.keys(data).length > 0 && me?.keys?.length > 0,
            rawCondition3: (!data || list?.length === 0) && (!me?.keys || me?.keys.length === 0),
            rawCondition4:
                list?.length === 0 && (!me?.flags || me?.flags.length === 0) && !me?.thumbnail && !ignoredProfile,
        }
    }, [list, data, me?.keys, me?.flags, me?.thumbnail, ignoredProfile])

    //      1) condition1 & condition2가 동시에 true면, condition3 & condition4 = false
    //      2) condition3가 true면 condition4는 false
    const {condition1, condition2, condition3, condition4} = useMemo(() => {
        let c1 = rawCondition1 // important alert
        let c2 = rawCondition2 // 데일리 리음트
        let c3 = rawCondition3 // 거래소 없음
        let c4 = rawCondition4 // profile 설정되있지 않음

        // condition1과 condition2가 동시에 true면 condition3,4는 false
        if (c1 && c2) {
            c3 = false
            c4 = false
        }
        // condition3가 true면 condition4는 무조건 false
        else if (c3) {
            c4 = false
        }

        if (isLoading) {
            return {condition1: false, condition2: false, condition3: false, condition4: false}
        }
        return {condition1: c1, condition2: c2, condition3: c3, condition4: c4}
    }, [rawCondition1, rawCondition2, rawCondition3, rawCondition4, isLoading])

    return (
        <>
            <section className={'gap-y-[10px] sm:gap-y-[7px] flex flex-col'}>
                {condition1 ? (
                    <div className={'gap-y-[10px] sm:gap-y-[7px] flex flex-col'}>
                        {list?.length > 1 ? (
                            <div
                                className={
                                    'items-center relative flex gap-x-[5px] sm:gap-x-[0px] py-[12px] px-[15px] sm:p-[10px] sm:pr-0 rounded-[5px] ring-[1px] ring-red_tint01 dark:ring-dark_red_tint01 bg-red_tint02 dark:bg-dark_red_tint02'
                                }>
                                <div
                                    className={
                                        'hidden sm:block absolute top-0 left-0 w-full h-full cursor-pointer rounded-[5px] z-[1]'
                                    }
                                    onClick={() => {
                                        importantAlertModalRef?.current?.show()
                                        createLog('event', 'account_alert_events_modal_open')
                                    }}
                                />
                                <IconNoticeRed
                                    className={'flex-none fill-red_shade dark:fill-dark_red_shade w-[20px] sm:w-[14px]'}
                                />
                                <TagText
                                    className={
                                        'text-body2 sm:text-body3 sm:!font-normal text-gray01 dark:text-dark_gray01 pl-[5px] sm:pl-[7px] grow'
                                    }>
                                    {t('accountAlert.counts', {count: list?.length})}
                                </TagText>
                                <WizButton
                                    buttonType={'border'}
                                    size={'small'}
                                    className={'h-fit my-auto w-[90px] sm:hidden flex-none'}
                                    text={t('accountAlert.button.more')}
                                    onClick={() => {
                                        importantAlertModalRef?.current?.show()
                                        createLog('event', 'account_alert_events_modal_open')
                                    }}
                                />
                                <div className={'cursor-pointer pl-[5px] pr-[10px] hidden sm:block'}>
                                    <IconNext
                                        className={
                                            'w-[12px] hidden sm:block flex-none fill-gray01 dark:fill-dark_gray01'
                                        }
                                    />
                                </div>
                            </div>
                        ) : (
                            list?.map((item, idx) => (
                                <div
                                    key={`account_alert_${idx}`}
                                    className={
                                        'items-center flex gap-x-[5px] sm:gap-x-[0px] py-[12px] px-[15px] sm:p-[10px] sm:pr-0 rounded-[5px] ring-[1px] ring-red_tint01 dark:ring-dark_red_tint01 bg-red_tint02 dark:bg-dark_red_tint02'
                                    }>
                                    <IconNoticeRed
                                        className={
                                            'flex-none fill-red_shade dark:fill-dark_red_shade w-[20px] sm:w-[14px]'
                                        }
                                    />
                                    <div className={'pl-[5px] sm:pl-[7px] grow'}>{item?.accountBoxView}</div>
                                    <WizButton
                                        buttonType={'border'}
                                        size={'small'}
                                        className={'h-fit my-auto w-[90px] sm:hidden flex-none'}
                                        text={item?.actionText}
                                        onClick={() => {
                                            item?.action()
                                            if (
                                                router?.asPath === '/account/setting' &&
                                                item?.type === 'email_connect'
                                            ) {
                                                changeEmailModalRef.current.show(true)
                                                createLog('event', 'account_alert_event', {type: item?.type})
                                            }
                                        }}
                                    />
                                    <div
                                        className={'cursor-pointer pl-[5px] pr-[10px] hidden sm:block'}
                                        onClick={() => {
                                            item?.action()
                                            if (
                                                router?.asPath === '/account/setting' &&
                                                item?.type === 'email_connect'
                                            ) {
                                                changeEmailModalRef.current.show(true)
                                                createLog('event', 'account_alert_event', {type: item?.type})
                                            }
                                        }}>
                                        <IconNext
                                            className={
                                                'w-[12px] hidden sm:block flex-none fill-gray01 dark:fill-dark_gray01'
                                            }
                                        />
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                ) : null}
                {condition2 ? (
                    <div>
                        <div
                            key={`account_alert_dailyReport`}
                            className={
                                'items-center cursor-pointer flex gap-x-[5px] sm:gap-x-[0px] py-[12px] px-[15px] sm:p-[10px] rounded-[5px] ring-[1px] ring-blue_tint01 dark:ring-dark_blue_tint01 bg-blue_tint02 dark:bg-dark_blue_tint02'
                            }
                            onClick={() => {
                                dailyReportModalRef?.current?.show('')
                                createLog('event', 'account_alert_dailyReport')
                            }}>
                            <Text className={'text-h3 sm:text-[14px] sm:leading-[18px]'}>
                                {data?.earning === 0 ? `💡` : data?.emoji}
                            </Text>
                            <div className={'pl-[5px] sm:pl-[7px] grow'}>
                                <Text
                                    className={
                                        'text-btn_sm sm:text-body_comm sm:hidden !font-normal text-gray02 dark:text-dark_gray02'
                                    }>
                                    {t('accountAlert.button.dailyReport.date', {
                                        date: formattedUTCOffset(data?.created_at),
                                    })}
                                </Text>
                                {data?.earning === 0 ? (
                                    <Text
                                        className={
                                            'text-body2 sm:sm:text-body3  text-gray01 dark:text-dark_gray01 grow '
                                        }>
                                        {t('accountAlert.button.dailyReport.same')}
                                    </Text>
                                ) : (
                                    <TagText
                                        className={
                                            'text-body2 sm:text-body3 sm:font-normal text-gray01 dark:text-dark_gray01 grow'
                                        }>
                                        {data?.earning >= 0
                                            ? t('accountAlert.button.dailyReport.increase', {
                                                  earning: renderUserProfitText,
                                              })
                                            : t('accountAlert.button.dailyReport.decrease', {
                                                  earning: renderUserProfitText,
                                              })}
                                    </TagText>
                                )}
                            </div>
                            <WizButton
                                buttonType={'border'}
                                size={'small'}
                                className={'h-fit my-auto w-[90px] sm:hidden flex-none'}
                                text={t('accountAlert.button.more')}
                            />
                            <IconNext
                                className={
                                    'w-[12px] hidden sm:block cursor-pointer sm:ml-[5px] flex-none fill-gray01 dark:fill-dark_gray01'
                                }
                            />
                        </div>
                        <div>
                            {isShowReportText === 'positive' && !condition3 && !condition4 && (
                                <Text
                                    className={
                                        'text-body2 sm:text-body3 text-gray02 dark:text-dark_gray02 text-center mt-[7px] sm:mt-[5px]'
                                    }>
                                    {t('accountAlert.dailyReport.positive')}
                                    {router?.locale === 'en' ? (
                                        <Span
                                            onClick={() => {
                                                sharePortfolioModalRef?.current?.show()
                                            }}
                                            className={'underline cursor-pointer'}>
                                            {t('accountAlert.dailyReport.positive.share')}
                                        </Span>
                                    ) : (
                                        ''
                                    )}
                                    {' ' + t('accountAlert.dailyReport.positive1')}
                                    {router?.locale === 'ko' ? (
                                        <Span
                                            onClick={() => {
                                                sharePortfolioModalRef?.current?.show()
                                            }}
                                            className={'underline cursor-pointer'}>
                                            {t('accountAlert.dailyReport.positive.share')}
                                        </Span>
                                    ) : (
                                        ''
                                    )}
                                </Text>
                            )}
                            {isShowReportText === 'negative' && !condition3 && !condition4 && (
                                <Text
                                    className={
                                        'text-body2 sm:text-body3 text-gray02 dark:text-dark_gray02 text-center mt-[7px] sm:mt-[5px]'
                                    }>
                                    {t('accountAlert.dailyReport.negative')}
                                    <Link
                                        href={getLinkUrl(URL_LEADERBOARD_COPY)}
                                        className={
                                            'hover:text-primary_shade dark:hover:text-dark_primary_shade underline'
                                        }>
                                        {t('accountAlert.dailyReport.negative.leaderboard')}
                                    </Link>
                                </Text>
                            )}
                        </div>
                    </div>
                ) : null}
                {condition3 && !isLoadingMe ? (
                    <div
                        key={`account_alert_connect_exchange`}
                        className={
                            'items-center cursor-pointer flex gap-x-[5px] sm:gap-x-[0px] py-[12px] px-[15px] sm:p-[10px] rounded-[5px] ring-[1px] ring-primary_tint03 dark:ring-dark_primary_tint03 bg-primary_tint04 dark:bg-dark_primary_tint04'
                        }
                        onClick={() => {
                            router?.push(getLinkUrl(URL_ACCOUNT_API))
                            createLog('event', 'account_alert_connect_api')
                        }}>
                        <Text className={'text-h3 sm:text-[14px] sm:leading-[18px]'}>📊</Text>
                        <div className={'pl-[5px] sm:pl-[7px] grow'}>
                            <Text
                                className={'text-btn_sm sm:text-body3 !font-normal text-gray01 dark:text-dark_gray01'}>
                                {t('accountAlert.exchange.text')}
                                <Span className={'underline cursor-pointer'}>{t('accountAlert.exchange.connect')}</Span>
                            </Text>
                        </div>
                        <IconNext
                            className={
                                'w-[12px] hidden sm:block sm:ml-[5px] cursor-pointer flex-none fill-gray01 dark:fill-dark_gray01'
                            }
                        />
                    </div>
                ) : null}
                {condition4 ? (
                    <div
                        key={`account_alert_profile`}
                        className={
                            'items-center flex gap-x-[5px] sm:gap-x-[0px] py-[12px] px-[15px] sm:p-[10px] sm:pr-[0px] rounded-[5px] bg-gray08 dark:bg-dark_gray08'
                        }>
                        <WizImage src={userRegion?.url} width={20} height={20} containerClassName={'flex-none'} />
                        <div
                            className={'pl-[5px] sm:pl-[7px] grow'}
                            onClick={() => {
                                createLog('event', 'account_alert_profile_setUp')
                            }}>
                            <TagText
                                className={'text-btn_sm sm:text-body3 !font-normal text-gray01 dark:text-dark_gray01'}>
                                {t('accountAlert.profile')}
                            </TagText>
                        </div>
                        <div
                            className={'cursor-pointer sm:pl-[5px] sm:pr-[10px] block'}
                            onClick={() => {
                                localStorage?.setItem('account_profile_setting_alert', 'true')
                                createLog('event', 'account_alert_profile_closed')
                                setIgnoredProfile(true)
                            }}>
                            <IconClose className={'fill-gray01 dark:fill-dark_gray01 w-[12px] cursor-pointer'} />
                        </div>
                    </div>
                ) : null}
            </section>
            <AccountImportantAlertModal ref={importantAlertModalRef} alertList={list} />
            <ChangeEmailModal ref={changeEmailModalRef} />
            <DailyReportModal ref={dailyReportModalRef} />
            <SharePortfolioOptionModal ref={sharePortfolioModalRef} />
        </>
    )
}

export default AccountImportantAlertContainer
